
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiPlus, FiEdit, FiTrash2, FiSave, FiX, FiUpload, FiEye, FiLayout, FiSettings, FiCheckCircle, FiGrid, FiBriefcase, FiAlertTriangle } from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../utils/api.js';

const Admin = () => {
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [posts, setPosts] = useState([]);
  const [skills, setSkills] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [resume, setResume] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: '', id: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, postsRes, skillsRes, experiencesRes, resumeRes, availabilityRes] = await Promise.all([
        api.get('/api/projects'),
        api.get('/api/posts?all=true'),
        api.get('/api/skills'),
        api.get('/api/experiences'),
        api.get('/api/resumes/latest').catch(() => ({ data: null })),
        api.get('/api/availability').catch(() => ({ data: null })),
      ]);
      setProjects(projectsRes.data);
      setPosts(postsRes.data);
      setSkills(skillsRes.data);
      setExperiences(experiencesRes.data);
      setResume(resumeRes.data);
      setAvailability(availabilityRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setProjects([]);
      setPosts([]);
      setSkills([]);
      setExperiences([]);
      setResume(null);
      setAvailability(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (type, id) => {
    setDeleteConfirm({ show: true, type, id });
  };

  const executeDelete = async () => {
    const { type, id } = deleteConfirm;
    try {
      await api.delete(`/api/${type}/${id}`);
      if (type === 'projects') {
        setProjects(projects.filter((p) => p._id !== id));
      } else if (type === 'posts') {
        setPosts(posts.filter((p) => p._id !== id));
      } else if (type === 'skills') {
        setSkills(skills.filter((s) => s._id !== id));
      } else if (type === 'experiences') {
        setExperiences(experiences.filter((e) => e._id !== id));
      } else if (type === 'resumes') {
        setResume(null);
      }
      toast.success(`${type.slice(0, -1)} deleted successfully`);
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    } finally {
      setDeleteConfirm({ show: false, type: '', id: '' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - Portfolio</title>
      </Helmet>

      <div className="pt-24 section-padding bg-dark-900 min-h-screen relative overflow-hidden">
        {/* Atmospheric Background Decorations */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-secondary_accent/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[120px] pointer-events-none" />

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
              <div>
                <h1 className="text-4xl font-bold text-white tracking-tight font-clash mb-2">Admin Portal</h1>
                <p className="text-gray-400 font-light">Manage your content, projects, and site performance.</p>
              </div>
              <div className="flex items-center gap-4 bg-white/5 border border-white/5 p-2 rounded-2xl backdrop-blur-md">
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary_accent/10 text-secondary_accent border border-secondary_accent/20">
                  <FiCheckCircle size={16} />
                  <span className="text-xs font-bold uppercase tracking-widest">System Online</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-10 p-2 bg-white/5 border border-white/5 rounded-[1.5rem] backdrop-blur-xl">
              {['projects', 'posts', 'skills', 'experiences', 'resume', 'availability'].map(tab => (
                <button
                  key={tab}
                  className={`px-6 py-3 rounded-xl text-xs font-bold uppercase tracking-widest transition-all duration-300 flex items-center gap-2 ${
                    activeTab === tab
                      ? 'bg-secondary_accent text-dark-900 shadow-lg shadow-secondary_accent/10'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === 'projects' && <FiGrid size={14} />}
                  {tab === 'posts' && <FiLayout size={14} />}
                  {tab === 'skills' && <FiSettings size={14} />}
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            <div className="glass-effect rounded-[2.5rem] p-8 border border-white/5 min-h-[500px] relative overflow-hidden group">
               {/* Subtle background decoration icon for the active tab */}
               <div className="absolute -right-20 -bottom-20 opacity-[0.02] group-hover:opacity-[0.04] transition-all duration-700 pointer-events-none">
                  {activeTab === 'projects' && <FiGrid size={400} />}
                  {activeTab === 'posts' && <FiLayout size={400} />}
                  {activeTab === 'availability' && <FiCheckCircle size={400} />}
               </div>

               <div className="relative z-10">
                {activeTab === 'projects' && (
                  <ProjectsTab
                    projects={projects}
                    onEdit={setEditingItem}
                    onDelete={(id) => handleDelete('projects', id)}
                    onAdd={() => setShowForm(true)}
                  />
                )}

                {activeTab === 'posts' && (
                  <PostsTab
                    posts={posts}
                    onEdit={setEditingItem}
                    onDelete={(id) => handleDelete('posts', id)}
                    onAdd={() => setShowForm(true)}
                  />
                )}

                {activeTab === 'skills' && (
                  <SkillsTab
                    skills={skills}
                    onEdit={setEditingItem}
                    onDelete={(id) => handleDelete('skills', id)}
                    onAdd={() => setShowForm(true)}
                  />
                )}

                {activeTab === 'experiences' && (
                  <ExperiencesTab
                    experiences={experiences}
                    onEdit={setEditingItem}
                    onDelete={(id) => handleDelete('experiences', id)}
                    onAdd={() => setShowForm(true)}
                  />
                )}

                {activeTab === 'resume' && (
                  <ResumeTab
                    resume={resume}
                    onUpload={() => setShowForm(true)}
                    onDelete={() => handleDelete('resumes', resume?._id)}
                  />
                )}

                {activeTab === 'availability' && (
                  <AvailabilityTab
                    availability={availability}
                    onEdit={() => {
                      setEditingItem(availability || { status: 'Not Available', message: '' });
                      setShowForm(true);
                    }}
                  />
                )}
               </div>
            </div>

            {(editingItem || showForm) && (
              <EditForm
                item={editingItem}
                type={activeTab}
                onSave={() => {
                  setEditingItem(null);
                  setShowForm(false);
                  fetchData();
                }}
                onCancel={() => {
                  setEditingItem(null);
                  setShowForm(false);
                }}
              />
            )}

            {deleteConfirm.show && (
              <div className="fixed inset-0 bg-dark-900/80 backdrop-blur-md flex items-center justify-center z-[110] p-4">
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="glass-effect max-w-sm w-full rounded-[2rem] border border-white/10 shadow-2xl p-8 text-center"
                >
                  <div className="w-16 h-16 rounded-2xl bg-red-500/10 text-red-500 flex items-center justify-center mx-auto mb-6">
                    <FiAlertTriangle size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 font-clash">Confirm Deletion</h3>
                  <p className="text-gray-400 text-sm font-light mb-8">
                    Are you sure you want to permanently remove this {deleteConfirm.type.slice(0, -1)}? This action cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <button 
                      onClick={() => setDeleteConfirm({ show: false, type: '', id: '' })}
                      className="flex-1 py-3 rounded-xl bg-white/5 text-gray-400 font-bold uppercase tracking-widest text-[10px] hover:text-white transition-all"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={executeDelete}
                      className="flex-1 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold uppercase tracking-widest text-[10px] transition-all shadow-lg shadow-red-500/20"
                    >
                      Yes, Delete
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

const ProjectsTab = ({ projects, onEdit, onDelete, onAdd }) => (
  <div>
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
      <div>
        <h2 className="text-2xl font-bold text-white tracking-tight">Project Management</h2>
        <p className="text-sm text-gray-400 font-light">Add or modify your portfolio projects.</p>
      </div>
      <button onClick={onAdd} className="btn-primary inline-flex items-center px-6 py-2.5 rounded-xl shadow-lg shadow-secondary_accent/10">
        <FiPlus className="mr-2" /> New Project
      </button>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {projects.length === 0 ? (
        <div className="col-span-full py-20 text-center bg-white/5 rounded-[2rem] border border-dashed border-white/10">
          <p className="text-gray-500 font-light">No projects found. Start by adding one!</p>
        </div>
      ) : (
        projects.map((project) => (
          <div key={project._id} className="p-6 rounded-[2rem] bg-white/5 border border-white/5 hover:border-secondary_accent/20 transition-all duration-300 group/card">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-bold text-white group-hover/card:text-secondary_accent transition-colors">{project.title}</h3>
              <div className="flex gap-2 opacity-0 group-hover/card:opacity-100 transition-opacity">
                <button
                  onClick={() => onEdit(project)}
                  className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-all"
                >
                  <FiEdit size={14} />
                </button>
                <button
                  onClick={() => onDelete(project._id)}
                  className="p-2 rounded-lg bg-white/5 text-red-400/50 hover:text-red-400 hover:bg-red-400/10 transition-all"
                >
                  <FiTrash2 size={14} />
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {project.technologies.slice(0, 4).map(tech => (
                <span key={tech} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 text-gray-500 border border-white/5">
                  {tech}
                </span>
              ))}
              {project.technologies.length > 4 && <span className="text-[10px] text-gray-600">+{project.technologies.length - 4} more</span>}
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-white/5">
              <span className="text-[10px] uppercase tracking-[0.2em] text-gray-600">Added {new Date(project.createdAt).toLocaleDateString()}</span>
              <span className={`text-[10px] font-bold uppercase tracking-widest ${project.status === 'active' ? 'text-green-500/60' : 'text-gray-500'}`}>
                {project.status}
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

const PostsTab = ({ posts, onEdit, onDelete, onAdd }) => (
  <div>
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
      <div>
        <h2 className="text-2xl font-bold text-white tracking-tight">Article Management</h2>
        <p className="text-sm text-gray-400 font-light">Draft and publish your latest blog posts.</p>
      </div>
      <button onClick={onAdd} className="btn-primary inline-flex items-center px-6 py-2.5 rounded-xl">
        <FiPlus className="mr-2" /> New Article
      </button>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {posts.length === 0 ? (
        <div className="col-span-full py-20 text-center bg-white/5 rounded-[2rem] border border-dashed border-white/10">
          <p className="text-gray-500 font-light">No posts found.</p>
        </div>
      ) : (
        posts.map((post) => (
          <div key={post._id} className="p-6 rounded-[2rem] bg-white/5 border border-white/5 hover:border-secondary_accent/20 transition-all duration-300 group/card">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold text-white group-hover/card:text-secondary_accent transition-colors mb-1">{post.title}</h3>
                <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">By {post.author}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => onEdit(post)} className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-white transition-all">
                  <FiEdit size={14} />
                </button>
                <button onClick={() => onDelete(post._id)} className="p-2 rounded-lg bg-white/5 text-red-400/50 hover:text-red-400 transition-all">
                  <FiTrash2 size={14} />
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-white/5">
              <span className="text-[10px] uppercase tracking-[0.2em] text-gray-600">{new Date(post.createdAt).toLocaleDateString()}</span>
              <span className={`text-[10px] font-bold uppercase tracking-widest ${post.published ? 'text-secondary_accent' : 'text-gray-600'}`}>
                {post.published ? 'Published' : 'Draft'}
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

const SkillsTab = ({ skills, onEdit, onDelete, onAdd }) => (
  <div>
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
      <div>
        <h2 className="text-2xl font-bold text-white tracking-tight">Skill Management</h2>
        <p className="text-sm text-gray-400 font-light">Organize your technical expertise categories.</p>
      </div>
      <button onClick={onAdd} className="btn-primary inline-flex items-center px-6 py-2.5 rounded-xl">
        <FiPlus className="mr-2" /> New Category
      </button>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {skills.map((skill) => (
        <div key={skill._id} className="p-6 rounded-[2rem] bg-white/5 border border-white/5 hover:border-secondary_accent/20 transition-all duration-300 group/card">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-base font-bold text-white tracking-tight">{skill.title}</h3>
            <div className="flex gap-1">
              <button onClick={() => onEdit(skill)} className="p-1.5 text-gray-500 hover:text-white transition-colors"><FiEdit size={12} /></button>
              <button onClick={() => onDelete(skill._id)} className="p-1.5 text-gray-500 hover:text-red-400 transition-colors"><FiTrash2 size={12} /></button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {skill.skills.map(s => (
              <span key={s} className="text-[9px] font-bold uppercase tracking-tighter px-2 py-0.5 rounded-md bg-white/5 text-gray-400 border border-white/5">{s}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ExperiencesTab = ({ experiences, onEdit, onDelete, onAdd }) => (
  <div>
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
      <div>
        <h2 className="text-2xl font-bold text-white tracking-tight">Experience Timeline</h2>
        <p className="text-sm text-gray-400 font-light">Update your professional career history.</p>
      </div>
      <button onClick={onAdd} className="btn-primary inline-flex items-center px-6 py-2.5 rounded-xl">
        <FiPlus className="mr-2" /> New Experience
      </button>
    </div>
    
    <div className="space-y-4">
      {experiences.map((exp) => (
        <div key={exp._id} className="p-6 rounded-[2rem] bg-white/5 border border-white/5 hover:border-secondary_accent/20 transition-all duration-300 group/card flex flex-col sm:flex-row justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-secondary_accent/10 text-secondary_accent group-hover/card:bg-secondary_accent group-hover/card:text-dark-900 transition-all duration-500">
               <FiBriefcase size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white tracking-tight mb-1">{exp.title}</h3>
              <p className="text-xs text-secondary_accent/80 font-medium tracking-widest uppercase">{exp.company} • {exp.period}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 self-end sm:self-center">
            <button onClick={() => onEdit(exp)} className="px-4 py-2 rounded-xl bg-white/5 text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-white transition-colors">Edit</button>
            <button onClick={() => onDelete(exp._id)} className="px-4 py-2 rounded-xl bg-red-500/10 text-xs font-bold uppercase tracking-widest text-red-400/60 hover:text-red-400 transition-colors">Delete</button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ResumeTab = ({ resume, onUpload, onDelete }) => (
  <div>
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
      <div>
        <h2 className="text-2xl font-bold text-white tracking-tight">Resume Asset</h2>
        <p className="text-sm text-gray-400 font-light">Upload or replace your professional CV.</p>
      </div>
      <button onClick={onUpload} className="btn-primary inline-flex items-center px-6 py-2.5 rounded-xl">
        <FiUpload className="mr-2" /> {resume ? 'Replace' : 'Upload'} Resume
      </button>
    </div>
    
    <div className="p-8 rounded-[2.5rem] bg-white/5 border border-white/10 relative overflow-hidden group">
      {/* Background Decor */}
      <div className="absolute -right-10 -bottom-10 opacity-[0.03] group-hover:opacity-[0.06] transition-all duration-700 pointer-events-none">
        <FiLayout size={200} />
      </div>

      {resume ? (
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-secondary_accent/10 flex items-center justify-center text-secondary_accent">
               <FiEye size={30} />
            </div>
            <div>
              <p className="text-lg font-bold text-white tracking-tight mb-1">{resume.originalName}</p>
              <div className="flex flex-wrap gap-4 text-xs text-gray-500 font-medium uppercase tracking-widest">
                <span>{(resume.size / 1024).toFixed(2)} KB</span>
                <span>•</span>
                <span>Uploaded {new Date(resume.uploadedAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`/api/resumes/download`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 rounded-xl bg-white/5 text-xs font-bold uppercase tracking-widest text-gray-300 hover:text-white transition-all"
            >
              Preview
            </a>
            <button
              onClick={onDelete}
              className="px-6 py-3 rounded-xl bg-red-500/10 text-xs font-bold uppercase tracking-widest text-red-400/60 hover:text-red-400 transition-all"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div className="py-10 text-center relative z-10">
          <p className="text-gray-500 font-light mb-0">No resume document uploaded yet.</p>
        </div>
      )}
    </div>
  </div>
);

const AvailabilityTab = ({ availability, onEdit }) => (
  <div>
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
      <div>
        <h2 className="text-2xl font-bold text-white tracking-tight">System Status</h2>
        <p className="text-sm text-gray-400 font-light">Update your current work availability.</p>
      </div>
      <button onClick={onEdit} className="btn-primary inline-flex items-center px-6 py-2.5 rounded-xl">
        <FiEdit className="mr-2" /> Update Status
      </button>
    </div>
    
    <div className="p-8 rounded-[2.5rem] bg-white/5 border border-white/10 relative overflow-hidden group">
      {/* Background Decor */}
      <div className="absolute -right-10 -bottom-10 opacity-[0.03] group-hover:opacity-[0.06] transition-all duration-700 pointer-events-none">
        <FiCheckCircle size={200} />
      </div>

      {availability ? (
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-3 h-3 rounded-full animate-pulse ${
              availability.status === 'Open to Work' ? 'bg-green-500' : 'bg-secondary_accent'
            }`} />
            <h3 className="text-2xl font-bold text-white tracking-tight">{availability.status}</h3>
          </div>
          {availability.message && (
            <div className="p-4 rounded-xl bg-white/5 border border-white/5 mb-6 max-w-2xl">
              <p className="text-gray-400 font-light italic leading-relaxed">"{availability.message}"</p>
            </div>
          )}
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-600 font-bold">
            Last Updated: {new Date(availability.updatedAt).toLocaleDateString()}
          </p>
        </div>
      ) : (
        <div className="py-10 text-center relative z-10">
          <p className="text-gray-500 font-light">No availability status defined.</p>
        </div>
      )}
    </div>
  </div>
);

const EditForm = ({ item, type, onSave, onCancel }) => {
  const isEditing = !!item;
  const [formData, setFormData] = useState({
    title: item?.title || '',
    excerpt: item?.excerpt || '',
    technologies: item?.technologies?.join(', ') || '',
    liveUrl: item?.liveUrl || '',
    githubUrl: item?.githubUrl || '',
    content: item?.content || '',
    tags: item?.tags?.join(', ') || '',
    image: item?.image || '',
    featured: item?.featured || false,
    status: item?.status || type === 'availability' ? 'Not Available' : 'active',
    skills: item?.skills?.join(', ') || '',
    author: item?.author || 'Admin',
    readTime: item?.readTime || '5 min read',
    published: item?.published ?? true,
    company: item?.company || '',
    period: item?.period || '',
    description: item?.description || '',
    resume: null,
    message: item?.message || '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEditing && type === 'posts') {
      const fetchPostData = async () => {
        setLoading(true);
        try {
          const response = await api.get(`/api/posts/edit/${item._id}`);
          const postData = response.data;
          setFormData((prevData) => ({
            ...prevData,
            title: postData.title || '',
            excerpt: postData.excerpt || '',
            content: postData.content || '',
            tags: postData.tags?.join(', ') || '',
            image: postData.image || '',
            author: postData.author || 'Admin',
            readTime: postData.readTime || '5 min read',
            published: postData.published ?? true,
          }));
        } catch (error) {
          console.error('Error fetching post for editing:', error);
          setError('Failed to load post data.');
        } finally {
          setLoading(false);
        }
      };
      fetchPostData();
    }
  }, [item, type, isEditing]);

  const handleChange = (e) => {
    const { name, value, type: inputType, checked, files } = e.target;
    if (inputType === 'file' && files[0] && files[0].size > 10 * 1024 * 1024) {
      setError('File size exceeds 10MB limit');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: inputType === 'checkbox' ? checked : inputType === 'file' ? files[0] : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let payload;
      if (type === 'resume') {
        if (!formData.resume) {
          setError('Resume file is required');
          setLoading(false);
          return;
        }
        const formPayload = new FormData();
        formPayload.append('resume', formData.resume);
        await api.post('/api/resumes', formPayload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        if (type === 'posts') {
          if (!formData.title.trim()) {
            setError('Title is required');
            setLoading(false);
            return;
          }
          if (!formData.excerpt.trim()) {
            setError('Excerpt is required');
            setLoading(false);
            return;
          }
          if (!formData.content.trim()) {
            setError('Content is required');
            setLoading(false);
            return;
          }
        } else if (type === 'skills') {
          if (!formData.title.trim() || !formData.skills.trim()) {
            setError('Title and skills are required');
            setLoading(false);
            return;
          }
        } else if (type === 'experiences') {
          if (!formData.title.trim() || !formData.company.trim() || !formData.period.trim() || !formData.description.trim()) {
            setError('All fields are required');
            setLoading(false);
            return;
          }
        } else if (type === 'availability') {
          if (!formData.status) {
            setError('Status is required');
            setLoading(false);
            return;
          }
        }

        payload = { title: formData.title };

        if (type === 'projects') {
          payload.description = formData.description;
          payload.technologies = formData.technologies
            .split(',')
            .map((tech) => tech.trim())
            .filter((tech) => tech);
          payload.image = formData.image;
          payload.liveUrl = formData.liveUrl;
          payload.githubUrl = formData.githubUrl;
          payload.featured = formData.featured;
          payload.status = formData.status;
        } else if (type === 'posts') {
          const lines = formData.content.split('\n').filter(line => line.trim());
          let htmlContent = '<div>';
          let inList = false;
          lines.forEach(line => {
            if (line.startsWith('## ')) {
              if (inList) {
                htmlContent += '</ul>';
                inList = false;
              }
              htmlContent += `<h2>${line.slice(3)}</h2>`;
            } else if (line.startsWith('- ')) {
              if (!inList) {
                htmlContent += '<ul>';
                inList = true;
              }
              htmlContent += `<li>${line.slice(2)}</li>`;
            } else if (line.trim()) {
              if (inList) {
                htmlContent += '</ul>';
                inList = false;
              }
              htmlContent += `<p>${line}</p>`;
            }
          });
          if (inList) {
            htmlContent += '</ul>';
          }
          htmlContent += '</div>';

          payload.excerpt = formData.excerpt;
          payload.content = htmlContent;
          payload.tags = formData.tags
            ?.split(',')
            .map((tag) => tag.trim())
            .filter((tag) => tag);
          payload.image = formData.image;
          payload.author = formData.author;
          payload.readTime = formData.readTime;
          payload.published = formData.published;
        } else if (type === 'skills') {
          payload.title = formData.title;
          payload.skills = formData.skills
            .split(',')
            .map((skill) => skill.trim())
            .filter((skill) => skill);
        } else if (type === 'experiences') {
          payload.company = formData.company;
          payload.period = formData.period;
          payload.description = formData.description;
        } else if (type === 'availability') {
          payload = {
            status: formData.status,
            message: formData.message,
          };
        }

        if (isEditing && type !== 'availability') {
          await api.put(`/api/${type}/${item._id}`, payload);
        } else {
          await api.post(`/api/${type}`, payload);
        }
      }

      toast.success(`${type.slice(0, -1)} ${isEditing ? 'updated' : 'created'} successfully`);
      onSave();
    } catch (error) {
      const errorMessage = error.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} ${type === 'resume' ? 'resume' : type === 'availability' ? 'availability' : type.slice(0, -1)}`;
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="fixed inset-0 bg-dark-900/80 backdrop-blur-md flex items-center justify-center z-[100] p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="glass-effect max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-[2.5rem] border border-white/10 shadow-2xl relative"
      >
        <div className="sticky top-0 z-20 px-8 py-6 bg-dark-900/50 backdrop-blur-xl border-b border-white/5 flex justify-between items-center">
          <h3 className="text-xl font-bold text-white tracking-tight font-clash">
            {isEditing ? 'Modify' : 'Initialize'} {titleCase(type === 'projects' ? 'Project' : type === 'posts' ? 'Post' : type === 'skills' ? 'Skill' : type === 'experiences' ? 'Experience' : type === 'resume' ? 'Resume' : 'Availability')}
          </h3>
          <button onClick={onCancel} className="p-2.5 rounded-xl bg-white/5 text-gray-400 hover:text-white transition-all">
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 md:p-8">
          <div className="space-y-6">
            {type !== 'resume' && type !== 'availability' && type !== 'skills' && (
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Title</label>
                <input
                  type="text"
                  name="title"
                  className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-secondary_accent/20 focus:border-secondary_accent/40 transition-all duration-300"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {type === 'projects' && (
              <>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Description</label>
                  <textarea
                    name="description"
                    className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white min-h-[120px] focus:outline-none focus:ring-2 focus:ring-secondary_accent/20 transition-all duration-300"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Technologies</label>
                  <input
                    type="text"
                    name="technologies"
                    className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-secondary_accent/20 transition-all"
                    value={formData.technologies}
                    onChange={handleChange}
                    placeholder="e.g. React, Node.js"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Live Demo</label>
                    <input
                      type="url"
                      name="liveUrl"
                      className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white focus:outline-none focus:ring-2"
                      value={formData.liveUrl}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">GitHub</label>
                    <input
                      type="url"
                      name="githubUrl"
                      className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white focus:outline-none focus:ring-2"
                      value={formData.githubUrl}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-6 py-2">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        name="featured"
                        checked={formData.featured}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className={`w-10 h-6 rounded-full transition-colors ${formData.featured ? 'bg-secondary_accent' : 'bg-white/10'}`} />
                      <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.featured ? 'translate-x-4' : ''}`} />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-widest text-gray-400 group-hover:text-white transition-colors">Featured</span>
                  </label>
                  
                  <div className="flex-1 space-y-2">
                    <select
                      name="status"
                      className="w-full bg-white/5 border border-white/5 rounded-xl py-2 px-4 text-xs font-bold uppercase tracking-widest text-gray-300 focus:outline-none"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="draft">Draft</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {type === 'posts' && (
              <>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Excerpt</label>
                  <textarea
                    name="excerpt"
                    className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white focus:outline-none focus:ring-2"
                    value={formData.excerpt}
                    onChange={handleChange}
                    rows={3}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Content</label>
                  <textarea
                    name="content"
                    className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white min-h-[300px] focus:outline-none focus:ring-2 font-mono text-sm"
                    value={formData.content}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Author</label>
                    <input type="text" name="author" className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white text-sm" value={formData.author} onChange={handleChange} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Read Time</label>
                    <input type="text" name="readTime" className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white text-sm" value={formData.readTime} onChange={handleChange} />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                   <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="published" checked={formData.published} onChange={handleChange} className="w-4 h-4 rounded bg-white/5 border-white/10 text-secondary_accent focus:ring-0" />
                      <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Published</span>
                   </label>
                </div>
              </>
            )}

            {type === 'skills' && (
              <>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Category Title</label>
                  <input type="text" name="title" className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white" value={formData.title} onChange={handleChange} required />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Skills (comma-separated)</label>
                  <input type="text" name="skills" className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white" value={formData.skills} onChange={handleChange} placeholder="React, Vue, Angular" required />
                </div>
              </>
            )}

            {type === 'experiences' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Position</label>
                    <input type="text" name="title" className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white" value={formData.title} onChange={handleChange} required />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Company</label>
                    <input type="text" name="company" className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white" value={formData.company} onChange={handleChange} required />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Period</label>
                  <input type="text" name="period" className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white" value={formData.period} onChange={handleChange} placeholder="2022 - Present" required />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Description</label>
                  <ReactQuill
                    value={formData.description}
                    onChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
                    className="bg-white/5 rounded-2xl overflow-hidden border border-white/5"
                    theme="snow"
                  />
                </div>
              </>
            )}

            {type === 'resume' && (
              <div className="space-y-4 py-6 text-center border-2 border-dashed border-white/10 rounded-[2rem] bg-white/5 group hover:border-secondary_accent/30 transition-all duration-500">
                <FiUpload className="mx-auto text-gray-600 group-hover:text-secondary_accent transition-colors" size={40} />
                <div className="space-y-1">
                  <p className="text-sm font-bold text-white tracking-tight">Select Resume PDF</p>
                  <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Max file size 10MB</p>
                </div>
                <input
                  type="file"
                  name="resume"
                  className="hidden"
                  id="resume-upload"
                  accept="application/pdf"
                  onChange={handleChange}
                  required
                />
                <label htmlFor="resume-upload" className="inline-flex px-6 py-2 rounded-xl bg-secondary_accent text-dark-900 text-xs font-bold uppercase tracking-widest cursor-pointer hover:bg-white transition-colors">
                  {formData.resume ? formData.resume.name : 'Browse Files'}
                </label>
              </div>
            )}

            {type === 'availability' && (
              <>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Current Status</label>
                  <select
                    name="status"
                    className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white focus:outline-none"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    <option value="Open to Work">Open to Work</option>
                    <option value="Available for Freelance">Available for Freelance</option>
                    <option value="Not Available">Not Available</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Bio Message</label>
                  <textarea
                    name="message"
                    className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white focus:outline-none min-h-[100px]"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                  />
                </div>
              </>
            )}

            {(type === 'projects' || type === 'posts') && (
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">Cover Image URL</label>
                <input type="url" name="image" className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white" value={formData.image} onChange={handleChange} />
              </div>
            )}

            {error && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold uppercase tracking-widest text-center">
                {error}
              </motion.div>
            )}
          </div>

          <div className="flex gap-4 pt-10 sticky bottom-0 bg-transparent">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-4 rounded-2xl bg-secondary_accent hover:bg-white text-dark-900 font-bold uppercase tracking-widest transition-all duration-300 shadow-lg shadow-secondary_accent/10 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-dark-900/30 border-t-dark-900 rounded-full animate-spin" />
              ) : (
                <>
                  <FiSave size={18} />
                  Authorize & Save
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-8 py-4 rounded-2xl bg-white/5 border border-white/5 text-gray-400 font-bold uppercase tracking-widest text-xs hover:text-white hover:bg-white/10 transition-all"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Utility function to convert string to title case
const titleCase = (str) => {
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

export default Admin;
